/**
 * @description Presentational Super Admin list chrome (column-def table)
 *
 * Key features:
 * - One wrapper / thead / tbody / empty box for every SA list
 * - Renders caller-supplied column defs (headers + cells)
 * - Optional row click and sortable headers
 *
 * @dependencies
 * - lucide-react: sort chevrons
 * - adminListColumns: ADMIN_LIST_CHROME class contract
 *
 * @notes
 * - Data pages stay responsible for fetch / filter / isolation
 * - Hosting and Features are column config, not a second markup tree
 * - Do not flip cpResolution
 */

import { ChevronDown, ChevronUp } from 'lucide-react';
import { ADMIN_LIST_CHROME } from './adminListColumns';

/**
 * @param {{ column: string, sortKey?: string, sortDir?: 'asc'|'desc' }} props
 */
function SortIcon({ column, sortKey, sortDir }) {
  if (sortKey !== column) return null;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 inline ml-1" />
    : <ChevronDown className="w-3 h-3 inline ml-1" />;
}

/**
 * @param {object} props
 * @param {Array<{ key: string, header: string, sortKey?: string, sortable?: boolean, cellClassName?: string, render: Function }>} props.columns
 * @param {object[]} props.rows
 * @param {(row: object) => string} props.rowKey
 * @param {(row: object) => void} [props.onRowClick]
 * @param {import('react').ComponentType<{ className?: string }>} [props.emptyIcon]
 * @param {string} [props.emptyMessage]
 * @param {string} [props.sortKey]
 * @param {'asc'|'desc'} [props.sortDir]
 * @param {(key: string) => void} [props.onSort]
 */
function AdminListTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyIcon: EmptyIcon,
  emptyMessage,
  sortKey,
  sortDir,
  onSort
}) {
  if (!rows.length) {
    return (
      <div className={ADMIN_LIST_CHROME.empty}>
        {EmptyIcon ? <EmptyIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" /> : null}
        <p className="text-gray-500 text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={ADMIN_LIST_CHROME.wrapper}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            {columns.map((column) => {
              const columnSortKey = column.sortKey || column.key;
              const sortable = Boolean(column.sortable && onSort);
              return (
                <th
                  key={column.key}
                  className={`${ADMIN_LIST_CHROME.th}${sortable ? ' cursor-pointer hover:text-gray-700 select-none' : ''}`}
                  onClick={sortable ? () => onSort(columnSortKey) : undefined}
                >
                  {column.header}
                  {sortable ? (
                    <SortIcon column={columnSortKey} sortKey={sortKey} sortDir={sortDir} />
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`hover:bg-gray-50 transition-colors${onRowClick ? ' cursor-pointer' : ''}`}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.cellClassName || ADMIN_LIST_CHROME.td}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminListTable;
